import React, { useEffect, useRef, useState } from 'react';
import { Box, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import LoaderCover from '../../../../atoms/LoaderCover';
import ShadowBlock from '../../../../atoms/ShadowBlock';
import Table from '../../../../atoms/Table/Table';
import EmptyBlock from '../../../../molecules/EmptyBlock';
import { apiClient } from '../../../../../lib/apiClient';
import notificationUtils from '../../../../../utils/notificationUtils';
import { activeProblemsTableColumns, entityOptions } from './ActiveProblems.constants';
import { FixProblemDialog } from '../FixProblemDialog';
import Field from '../../../../atoms/Field';
import SearchInputWithLabel from '../../../../atoms/SearchInputWithLabel/SearchInputWithLabel';
import MButton from '../../../../MUI/Button/MButton';
import Form from '../../../../atoms/Form';
import { GeneralIntegrationInfo } from './GeneralIntegrationInfo';
import Dropdown from '../../../../atoms/Dropdown';

export const ActiveProblems = () => {
  const form = useForm({
    mode: 'onChange',
    defaultValues: {
      name: '',
      entity: '',
    },
  });

  const timerRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [tableLoading, setIsTableLoader] = useState(false);
  const [isGeneralInfoLoading, setIsGeneralInfoLoading] = useState(false);

  const [activeProblems, setActiveProblems] = useState(null);
  const [fixedProblem, setFixedProblem] = useState(null);
  const [generalInfo, setGeneralInfo] = useState(null);

  const [isFixProblemDialogOpen, setIsFixProblemDialogOpen] = useState(false);

  const formValues = form.watch();

  const handleFetchDataPage = async (currentPage = 1) => {
    setIsTableLoader(true);

    const params = {
      pageSize: 10,
      currentPage,
      entity: formValues?.entity?.value ?? undefined,
      name: formValues?.name || undefined,
    };

    try {
      const res = await apiClient.post(`/api/quickBooksMappingProblems/filter`, params);

      setActiveProblems(res.data.result?.items?.length ? res.data.result : []);
    } catch {
      notificationUtils.error('Failed, try again later');
    } finally {
      setIsTableLoader(false);
    }
  };

  const handleFetchLastProcessPage = async () => {
    setIsGeneralInfoLoading(true);

    try {
      const res = await apiClient.get(`/api/quickBooksMappingProblems/lastProcess`);
      const result = res.data?.result;

      if (result?.status !== 'Finished') {
        timerRef.current = setTimeout(handleFetchLastProcessPage, 2000);
      } else {
        setGeneralInfo(result ?? null);
        await handleFetchDataPage();
        setIsGeneralInfoLoading(false);
      }
    } catch {
      notificationUtils.error('Failed, try again later');
      setIsGeneralInfoLoading(false);
    }
  };

  const makeFixProblem = (obj) => {
    setFixedProblem(obj);
    setIsFixProblemDialogOpen(true);
  };

  const handleCloseFixProblemDialog = () => {
    setFixedProblem(null);
    setIsFixProblemDialogOpen(false);
  };

  const onConfirmFixProblem = async ({ problemId, quickBooksId }) => {
    setIsLoading(true);
    try {
      const params = {
        problemId,
        quickBooksId,
      };
      await apiClient.post(`/api/quickBooksMappingProblems/fix`, params);

      await handleFetchDataPage();
    } catch (e) {
      notificationUtils.error('Failed, try again later');
    }
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      handleFetchDataPage();
    } catch (e) {
      notificationUtils.error('Unknown record name');
      setActiveProblems(null);
    }
    setIsLoading(false);
  };

  const onCheckNowClick = async () => {
    setIsGeneralInfoLoading(true);
    try {
      await apiClient.post(`/api/quickBooksMappingProblems/startProcess`);

      await handleFetchLastProcessPage();
    } catch {
      notificationUtils.error('Failed, try again later');
      setIsGeneralInfoLoading(false);
    }
  };

  useEffect(() => {
    handleFetchLastProcessPage();
  }, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, []);

  return (
    <Stack>
      <GeneralIntegrationInfo
        onCheckNowClick={onCheckNowClick}
        lastRunTimestamp={generalInfo?.lastRunTimestamp}
        problemsDetectedCount={generalInfo?.problemsDetectedCount}
        isLoading={isGeneralInfoLoading}
      />
      <Form form={form} onSubmit={handleSubmit}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap">
          <Stack direction="row" gap={3} alignItems="center">
            <Field
              name="name"
              render={({ field, onCustomChange }) => (
                <SearchInputWithLabel
                  label="Record Name"
                  placeholder="Enter record name"
                  onChangeValue={onCustomChange(field.onChange)}
                  name="name"
                  value={field.value}
                  width={300}
                />
              )}
            />
            <Field
              name="entity"
              render={({ field, onCustomChange }) => {
                return (
                  <Dropdown
                    value={field.value}
                    isColumn
                    width="250px"
                    placeholder="Select entity type"
                    options={entityOptions}
                    onChange={onCustomChange(field.onChange)}
                    height="44px"
                    isClearable
                  />
                );
              }}
            />
            <MButton data-testid="apply_btn" variant="secondary" type="submit" size="medium">
              Apply
            </MButton>
          </Stack>
        </Stack>
      </Form>
      {isLoading && <LoaderCover />}
      {activeProblems && !isLoading && (
        <Box width="100%" margin="0 auto" mt={2}>
          <ShadowBlock>
            <Stack position="relative">
              {activeProblems?.items?.length ? (
                <Box display="flex" alignItems="center" pb={4}>
                  <Table
                    columns={activeProblemsTableColumns}
                    rows={activeProblems.items ?? []}
                    total={activeProblems?.totalRowsCount}
                    isPagination={activeProblems?.totalPages > 1}
                    onNextPage={handleFetchDataPage}
                    onPreviousPage={handleFetchDataPage}
                    onGotoPage={handleFetchDataPage}
                    onRefreshPage={handleFetchDataPage}
                    pageSize={10}
                    loading={tableLoading}
                    overflowHidden={false}
                    onFixProblem={makeFixProblem}
                  />
                </Box>
              ) : (
                <Box display="flex" justifyContent="center" alignItems="center" width="100%">
                  <EmptyBlock title="No Records" />
                </Box>
              )}
            </Stack>
          </ShadowBlock>
        </Box>
      )}
      {isFixProblemDialogOpen && (
        <FixProblemDialog
          onConfirm={onConfirmFixProblem}
          onClose={handleCloseFixProblemDialog}
          isOpen={isFixProblemDialogOpen}
          oldValue={fixedProblem}
        />
      )}
    </Stack>
  );
};
